// Ordrer (/ordre), i startsidens stil (planer/ny-kundesamhandling-plan.md). Viser den valgte
// ansattes aktive ordrer øverst, og under alle ordrer i butikken med søk, statusfilter, «søk i
// alle statuser», sortering og sider. Kommentaren vises på raden (før bare ved musepekeren).
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { format, parse, isValid } from 'date-fns';
import { Tilbake, Sidetopp, Valgknapper, ORDRESTATUSER, ordreStatusTekst, feltKlasse, knappSekundar } from './ny-kundesamhandling/Skjema';
import { FARGER, STI } from './ny-kundesamhandling/handlinger';
import Ikon from './ny-kundesamhandling/Ikon';
import { hentMineOrdrer, erGammel, virkedagerTekst, visDato } from './utils/mineOrdrer';
import { erSkiplukk, skiplukkTittel, parTekst } from './skiplukk/felles';
import { statusFor } from './tilbud/kalkyle';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Ordrene har dato som «d.M.yyyy, HH:mm:ss» (CreateOrder); eldre kan være ISO.
function tilDato(dateString) {
  if (!dateString) return null;
  const egen = parse(dateString, 'd.M.yyyy, HH:mm:ss', new Date());
  if (isValid(egen)) return egen;
  const iso = new Date(dateString);
  return isValid(iso) ? iso : null;
}

// Nye meldinger fra kunder på ordrer (src/routes/ordreMeldinger.js), øverst i ordrelisten.
// Flisen «Ordrevarsler» i kontrollpanelet lenker hit (#ordrevarsler). Ikke i varslingssenteret.
function OrdreVarsler() {
  const [varsler, setVarsler] = useState(null);
  useEffect(() => {
    let avbrutt = false;
    const hent = () =>
      fetch(`${API_BASE_URL}/orders/varsler?t=${Date.now()}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!avbrutt) setVarsler(d); })
        .catch(() => {});
    hent();
    const id = setInterval(hent, 60000);
    return () => { avbrutt = true; clearInterval(id); };
  }, []);
  if (!varsler?.antall) return null;
  return (
    <section id="ordrevarsler" aria-labelledby="ol-varsler" className="space-y-2 rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 sm:p-3">
      <h2 id="ol-varsler" className="flex items-center gap-2 text-base font-bold text-amber-950">
        <Ikon navn="melding" className="w-6 h-6" />
        Nye meldinger fra kunder ({varsler.antall})
      </h2>
      <ul className="space-y-2">
        {varsler.ordrer.map((o) => (
          <li key={o._id}>
            <Link
              to={`/order-details/${o._id}`}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-amber-200 bg-white px-3 py-1.5 text-slate-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            >
              <span className="font-semibold">{o.type === 'skiplukk' ? 'Skiplukk' : 'Ordre'} {o.ordreid}</span>
              <span className="min-w-0 break-words">{[o.kunde, o.produkt].filter(Boolean).join(' · ')}</span>
              <span className="ml-auto text-sm text-amber-900">
                {o.uleste} {o.uleste === 1 ? 'ny melding' : 'nye meldinger'}{o.siste ? ` · ${formatDateSafe(o.siste)}` : ''}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatDateSafe(dateString) {
  if (!dateString) return 'Ukjent dato';
  const d = tilDato(dateString);
  return d ? format(d, 'd.M.yyyy HH:mm') : 'Ugyldig dato';
}

const statusTekst = ordreStatusTekst;

function StatusMerke({ status }) {
  const cls = status === 'Avsluttet'
    ? 'bg-slate-200 text-slate-800'
    : status === 'AktivKLUBB'
    ? 'bg-violet-100 text-violet-900'
    : 'bg-emerald-100 text-emerald-900';
  return <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold ${cls}`}>{statusTekst(status)}</span>;
}

// Skiplukk merkes tydelig, så det ikke forveksles med en vareordre.
function SkiplukkMerke() {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-teal-100 px-2.5 py-0.5 text-sm font-semibold text-teal-900">
      <Ikon navn="snofnugg" className="w-4 h-4" /> Skiplukk
    </span>
  );
}

const ordreTittel = (order) =>
  erSkiplukk(order) ? skiplukkTittel(order) : [order.Varemerke, order.Produkt].filter(Boolean).join(' ');

const TYPEVALG = [
  { verdi: 'alle', tittel: 'Alle' },
  { verdi: 'ordre', tittel: 'Ordrer' },
  { verdi: 'skiplukk', tittel: 'Skiplukk' },
];

const KOLONNER = [
  { key: 'ordreid', tittel: 'Nr.' },
  { key: 'Varemerke', tittel: 'Vare' },
  { key: 'Størrelse', tittel: 'Str.', bred: true },
  { key: 'Farge', tittel: 'Farge', bred: true },
  { key: 'Status', tittel: 'Status' },
  { key: 'Ansatt', tittel: 'Ansatt', bred: true },
  { key: 'RegistrertDato', tittel: 'Registrert', bred: true },
];

function OrderList() {
  const [allOrders, setAllOrders] = useState([]); // alle ordre for valgt butikk (alle statuser)
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [statusFilter, setStatusFilter] = useState('Aktiv');
  const [typeFilter, setTypeFilter] = useState('alle');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(25);
  const [searchAllStatuses, setSearchAllStatuses] = useState(() => { // preferanse lagret i cookie
    const v = Cookies.get('orderSearchAllStatuses');
    return v ? v === 'true' : false;
  });

  const navigate = useNavigate();

  const selectedEmployee = Cookies.get('selectedEmployee') || '';
  const butikkid = parseInt(Cookies.get('butikkid'), 10) || null;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/orders`);
        if (!response.ok) {
          console.error('Feil ved henting av ordrer:', response.status);
          setFeil('Klarte ikke å hente ordrene. Last siden på nytt om litt.');
          return;
        }
        const data = await response.json();
        // Begrens alle ordre til valgt butikkid (uavhengig av status)
        setAllOrders(data.filter(order => order.butikkid === butikkid));
        setFeil('');
      } catch (error) {
        console.error('Feil ved kommunikasjon med serveren:', error);
        setFeil('Fikk ikke kontakt med serveren. Last siden på nytt om litt.');
      } finally {
        setLastet(true);
      }
    };

    fetchOrders();
  }, [butikkid]);

  // Alder i virkedager på den ansattes åpne ordrer (regnet på serveren, med helligdager), for
  // varseltrekanten på ordre eldre enn GAMMEL_ETTER virkedager, eller datoen varselet er utsatt til.
  const [alder, setAlder] = useState({});
  useEffect(() => {
    hentMineOrdrer(selectedEmployee).then((liste) => {
      if (liste) setAlder(Object.fromEntries(liste.map((o) => [o._id, o])));
    });
  }, [selectedEmployee]);

  // Ansattes aktive ordrer (ikke avsluttet), eldste først. Også skiplukk den ansatte følger opp.
  const employeeOrders = useMemo(
    () => allOrders
      .filter(order => (order.Ansatt === selectedEmployee || order.Oppfolger === selectedEmployee) && order.Status !== 'Avsluttet')
      .sort((a, b) => (tilDato(a.RegistrertDato) || 0) - (tilDato(b.RegistrertDato) || 0)),
    [allOrders, selectedEmployee]
  );

  // Statusfilter, så søk (i statusgrunnlaget eller i alle statuser), så sortering.
  const filteredOrders = useMemo(() => {
    const avType = allOrders.filter((order) =>
      typeFilter === 'alle' || (typeFilter === 'skiplukk' ? erSkiplukk(order) : !erSkiplukk(order))
    );
    const statusList = avType.filter(order =>
      (statusFilter === 'Aktiv' && order.Status !== 'Avsluttet') ||
      (statusFilter === 'AktivKLUBB' && order.Status === 'AktivKLUBB') ||
      (statusFilter === 'Avsluttet' && order.Status === 'Avsluttet')
    );
    const q = searchQuery.trim().toLowerCase();
    const base = q && searchAllStatuses ? avType : statusList;
    const treff = !q ? base : base.filter(order =>
      order.ordreid?.toString().includes(q) ||
      order.Varemerke?.toLowerCase().includes(q) ||
      order.Produkt?.toLowerCase().includes(q) ||
      order.Størrelse?.toLowerCase().includes(q) ||
      order.Farge?.toLowerCase().includes(q) ||
      order.Kommentar?.toLowerCase().includes(q) ||
      order.Status?.toLowerCase().includes(q) ||
      order.Ansatt?.toLowerCase().includes(q) ||
      order.Oppfolger?.toLowerCase().includes(q) ||
      (erSkiplukk(order) && ['skiplukk', ...(order.skiplukk?.par || []).map(parTekst)].join(' ').toLowerCase().includes(q))
    );
    const { key, direction } = sortConfig;
    if (!key) return treff;
    const retning = direction === 'ascending' ? 1 : -1;
    return [...treff].sort((a, b) => {
      if (key === 'ordreid' || key === 'butikkid') return retning * ((a[key] || 0) - (b[key] || 0));
      if (key === 'RegistrertDato') return retning * ((tilDato(a[key]) || 0) - (tilDato(b[key]) || 0));
      const valueA = a[key] ? a[key].toString().toLowerCase() : '';
      const valueB = b[key] ? b[key].toString().toLowerCase() : '';
      return retning * valueA.localeCompare(valueB, 'nb');
    });
  }, [allOrders, typeFilter, statusFilter, searchQuery, searchAllStatuses, sortConfig]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleToggleSearchAllStatuses = () => {
    const newVal = !searchAllStatuses;
    setSearchAllStatuses(newVal);
    Cookies.set('orderSearchAllStatuses', String(newVal));
    setCurrentPage(1);
  };

  const handleStatusChange = (nyStatus) => {
    setStatusFilter(nyStatus);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
    setSortConfig({ key, direction });
  };

  // Paginering
  const totalOrders = filteredOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalOrders / ordersPerPage));
  const side = Math.min(currentPage, totalPages);
  const indexOfLastOrder = side * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Tilbake />

      <Sidetopp ikon="bunke" farge="betaling" tittel="Ordreliste" undertittel="Varer bestilt til kunder">
        <Link
          to={`${STI}/ny-ordre`}
          className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-2xl border-2 border-amber-300 bg-amber-50 px-3 py-1.5 font-bold text-slate-900 transition hover:bg-amber-100 active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
        >
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${FARGER.betaling.ikon}`}>
            <Ikon navn="pluss" className="w-6 h-6" />
          </span>
          Registrer ordre
        </Link>
      </Sidetopp>

      <OrdreVarsler />

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {/* Den valgte ansattes aktive ordrer */}
      <section aria-labelledby="ol-mine" className="space-y-2">
        <h2 id="ol-mine" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
          Dine aktive ordrer{selectedEmployee ? ` (${selectedEmployee})` : ''}
          {lastet && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-semibold text-amber-900">{employeeOrders.length}</span>}
        </h2>
        <p className="text-slate-700">
          Sørg for å følge opp dine ordre her. Dersom andre skal følge opp, avtal dette med vedkommende som kan flytte
          denne over på sin bruker.
        </p>
        {!lastet ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter ordrer …</p>
        ) : !selectedEmployee ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Velg hvem du er øverst for å se dine ordrer.</p>
        ) : employeeOrders.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Du har ingen aktive ordrer.</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {employeeOrders.map((order) => (
              <li key={order._id} className="min-w-0">
                <Link
                  to={`/order-details/${order._id}`}
                  // Kommentaren vises ved hover (og leses av skjermleser), så kortene holdes små.
                  title={order.Kommentar || undefined}
                  className="group flex h-full flex-col gap-0.5 rounded-lg border-2 border-slate-200 bg-white px-2.5 py-1.5 text-sm transition hover:border-amber-300 hover:bg-amber-50/40 active:scale-[0.99] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
                >
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-xs font-semibold text-slate-600">Nr. {order.ordreid}</span>
                    {erSkiplukk(order) && <SkiplukkMerke />}
                    <StatusMerke status={order.Status} />
                  </span>
                  <span className="font-bold leading-tight text-slate-900 line-clamp-2 [overflow-wrap:anywhere]">
                    {ordreTittel(order) || 'Uten navn'}
                  </span>
                  {order.tilbud?.id && (
                    <span className="font-semibold text-amber-900">Pristilbud: {statusFor(order.tilbud.status).navn.toLowerCase()}</span>
                  )}
                  {erSkiplukk(order) && (
                    <span className="text-slate-700 [overflow-wrap:anywhere]">
                      {order.Oppfolger === selectedEmployee
                        ? `Du følger opp · ansvarlig: ${order.Ansatt || 'ukjent'}`
                        : order.Oppfolger ? `Du er ansvarlig · følges opp av ${order.Oppfolger}` : 'Du er ansvarlig · ingen oppfølger valgt'}
                    </span>
                  )}
                  {(order.Størrelse || order.Farge) && (
                    <span className="text-slate-700">{[order.Størrelse && `Str. ${order.Størrelse}`, order.Farge].filter(Boolean).join(' · ')}</span>
                  )}
                  {order.Kommentar && <span className="sr-only">Kommentar: {order.Kommentar}</span>}
                  {alder[order._id]?.utsatt ? (
                    <span className="flex items-center gap-1 font-semibold text-sky-900">
                      <Ikon navn="kalenderKlokke" className="w-4 h-4 shrink-0" /> Varsel utsatt til {visDato(alder[order._id].varselUtsatt.til)}
                    </span>
                  ) : alder[order._id] && erGammel(alder[order._id]) && (
                    <span className="flex items-center gap-1 font-semibold text-red-800">
                      <Ikon navn="varsel" className="w-4 h-4 shrink-0" /> Åpen i {virkedagerTekst(alder[order._id].virkedager)}
                    </span>
                  )}
                  <span className="mt-auto flex items-center justify-between gap-2 text-xs text-slate-600">
                    <span className="flex items-center gap-1.5">
                      {formatDateSafe(order.RegistrertDato)}
                      {order.Kommentar && <Ikon navn="lapp" className="w-3.5 h-3.5 shrink-0 text-slate-500" />}
                    </span>
                    <Ikon navn="pil" className="w-4 h-4 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Alle ordrer i butikken */}
      <section aria-labelledby="ol-alle" className="space-y-3">
        <h2 id="ol-alle" className="text-lg font-bold text-slate-900">Alle ordrer</h2>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div>
            <label htmlFor="ol-sok" className="mb-1 block font-semibold text-slate-800">Søk i ordrene</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                <Ikon navn="sok" className="w-6 h-6" />
              </span>
              <input
                id="ol-sok"
                type="search"
                inputMode="search"
                autoComplete="off"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Nr., merke, produkt, størrelse, farge, kommentar, status eller ansatt"
                aria-describedby="ol-sok-status"
                className={`${feltKlasse} pl-12`}
              />
            </div>
            <p id="ol-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
              {searchQuery.trim() && lastet ? `${totalOrders} ${totalOrders === 1 ? 'ordre' : 'ordrer'} passer` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <Valgknapper id="ol-type" etikett="Type" valg={TYPEVALG} verdi={typeFilter} onVelg={(v) => { setTypeFilter(v); setCurrentPage(1); }} />
            <Valgknapper id="ol-status" etikett="Status" valg={ORDRESTATUSER} verdi={statusFilter} onVelg={handleStatusChange} />
            <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 font-medium text-slate-800">
              <input
                type="checkbox"
                checked={searchAllStatuses}
                onChange={handleToggleSearchAllStatuses}
                className="h-5 w-5 cursor-pointer"
              />
              Søk i alle statuser
            </label>
          </div>
        </div>

        {lastet && totalOrders === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
            {searchQuery.trim() ? 'Ingen ordrer passer søket.' : 'Ingen ordrer med denne statusen.'}
          </p>
        ) : (
          <div tabIndex={0} role="region" aria-label="Alle ordrer, kan rulles sidelengs" className="overflow-x-auto rounded-2xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
            <table className="min-w-full text-left">
              <caption className="sr-only">Alle ordrer. Trykk på en kolonneoverskrift for å sortere.</caption>
              <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
                <tr>
                  {KOLONNER.map((k) => (
                    <th
                      key={k.key}
                      scope="col"
                      aria-sort={sortConfig.key === k.key ? sortConfig.direction : undefined}
                      className={`px-2 py-1.5 ${k.bred ? 'hidden md:table-cell' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(k.key)}
                        className="inline-flex min-h-[2.5rem] items-center gap-1 rounded-lg px-2 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                      >
                        {k.tittel}
                        {sortConfig.key === k.key && (
                          <Ikon navn="ned" className={`w-4 h-4 ${sortConfig.direction === 'ascending' ? 'rotate-180' : ''}`} />
                        )}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-slate-800">
                {currentOrders.map((order) => (
                  <tr
                    key={order._id}
                    className="cursor-pointer align-top hover:bg-amber-50/50"
                    onClick={() => navigate(`/order-details/${order._id}`)}
                  >
                    <td className="px-3 py-1.5 font-mono text-sm">
                      <Link
                        to={`/order-details/${order._id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900"
                      >
                        {order.ordreid}
                      </Link>
                    </td>
                    <td className="min-w-[12rem] px-3 py-1.5">
                      {erSkiplukk(order) && <span className="mb-1 block"><SkiplukkMerke /></span>}
                      <span className="block font-semibold [overflow-wrap:anywhere]">{ordreTittel(order)}</span>
                      {erSkiplukk(order) && order.Oppfolger && <span className="block text-sm text-slate-700">Følges opp av {order.Oppfolger}</span>}
                      {order.tilbud?.id && <span className="block text-sm font-semibold text-amber-900">Pristilbud: {statusFor(order.tilbud.status).navn.toLowerCase()}</span>}
                      <span className="block text-sm text-slate-600 md:hidden">{[order.Størrelse, order.Farge, order.Ansatt].filter(Boolean).join(' · ')}</span>
                      {order.Kommentar && <span className="block text-sm text-slate-600 line-clamp-2 [overflow-wrap:anywhere]">{order.Kommentar}</span>}
                    </td>
                    <td className="hidden px-3 py-1.5 md:table-cell">{order.Størrelse}</td>
                    <td className="hidden px-3 py-1.5 md:table-cell">{order.Farge}</td>
                    <td className="px-3 py-1.5"><StatusMerke status={order.Status} /></td>
                    <td className="hidden px-3 py-1.5 md:table-cell">{order.Ansatt}</td>
                    <td className="hidden whitespace-nowrap px-3 py-1.5 text-sm md:table-cell">{formatDateSafe(order.RegistrertDato)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginering */}
        {totalOrders > 0 && (
          <nav aria-label="Sider" className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <label htmlFor="ol-per-side" className="font-medium text-slate-800">Vis per side</label>
              <select
                id="ol-per-side"
                value={ordersPerPage}
                onChange={(e) => { setOrdersPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }}
                className="min-h-[2.25rem] rounded-xl border-2 border-slate-300 bg-white px-3"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <p className="text-slate-700">
              Viser {indexOfFirstOrder + 1}–{Math.min(indexOfLastOrder, totalOrders)} av {totalOrders} ordrer
            </p>
            {totalPages > 1 && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setCurrentPage(side - 1)} disabled={side === 1} className={knappSekundar}>
                  Forrige
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentPage(index + 1)}
                    aria-current={side === index + 1 ? 'page' : undefined}
                    aria-label={`Side ${index + 1}`}
                    className={`min-h-[2.25rem] min-w-[2.25rem] rounded-xl border-2 font-semibold focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
                      side === index + 1 ? 'border-indigo-700 bg-indigo-700 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button type="button" onClick={() => setCurrentPage(side + 1)} disabled={side === totalPages} className={knappSekundar}>
                  Neste
                </button>
              </div>
            )}
          </nav>
        )}
      </section>
    </div>
  );
}

export default OrderList;
